import PropTypes from 'prop-types';
import { PureComponent } from 'react';

import { Helmet } from 'react-helmet';

import { connect } from 'react-redux';

import { resetCompose } from 'mastodon/actions/compose';
import Base from 'mastodon/components/modal_root';
import FollowersModal from 'mastodon/features/account/components/followers_modal';
import SyncModal from 'mastodon/features/account/components/sync_modal';
import { AltTextModal } from 'mastodon/features/alt_text_modal';
import { ImageEditModal } from 'mastodon/features/image_edit';
import FavouritesModal from 'mastodon/features/status/components/favourites_modal';
import {
  MuteModal,
  BlockModal,
  DomainBlockModal,
  ReportModal,
  EmbedModal,
  ListAdder,
  CompareHistoryModal,
  FilterModal,
  InteractionModal,
  SubscribedLanguagesModal,
  ClosedRegistrationsModal,
  IgnoreNotificationsModal,
  AnnualReportModal,
} from 'mastodon/features/ui/util/async-components';
import { WithOptionalRouterPropTypes, withOptionalRouter } from 'mastodon/utils/react_router';
import { getScrollbarWidth } from 'mastodon/utils/scrollbar';

import BundleContainer from '../containers/bundle_container';

import ActionsModal from './actions_modal';
import AudioModal from './audio_modal';
import { BoostModal } from './boost_modal';
import ComposeFormModal from './compose_form_modal';
import {
  ConfirmationModal,
  ConfirmDeleteStatusModal,
  ConfirmDeleteListModal,
  ConfirmReplyModal,
  ConfirmEditStatusModal,
  ConfirmUnfollowModal,
  ConfirmClearNotificationsModal,
  ConfirmLogOutModal,
  ConfirmFollowToListModal,
  ConfirmMissingAltTextModal,
} from './confirmation_modals';
import ImageModal from './image_modal';
import LanguageModal from './language_modal';
import { LaoIdProfileModal } from './laoid_profile_modal';
import MediaModal from './media_modal';
import { ModalPlaceholder } from './modal_placeholder';
import ReportSensitiveModal from './report_sensitive_modal';
import ShareModal from './share_modal';
import ThemeModal from './theme_modal';
import VideoModal from './video_modal';

export const MODAL_COMPONENTS = {
  'MEDIA': () => Promise.resolve({ default: MediaModal }),
  'VIDEO': () => Promise.resolve({ default: VideoModal }),
  'AUDIO': () => Promise.resolve({ default: AudioModal }),
  'IMAGE': () => Promise.resolve({ default: ImageModal }),
  'BOOST': () => Promise.resolve({ default: BoostModal }),
  'SHARE': () => Promise.resolve({ default: ShareModal }),
  'CONFIRM': () => Promise.resolve({ default: ConfirmationModal }),
  'CONFIRM_DELETE_STATUS': () => Promise.resolve({ default: ConfirmDeleteStatusModal }),
  'CONFIRM_DELETE_LIST': () => Promise.resolve({ default: ConfirmDeleteListModal }),
  'CONFIRM_REPLY': () => Promise.resolve({ default: ConfirmReplyModal }),
  'CONFIRM_EDIT_STATUS': () => Promise.resolve({ default: ConfirmEditStatusModal }),
  'CONFIRM_UNFOLLOW': () => Promise.resolve({ default: ConfirmUnfollowModal }),
  'CONFIRM_CLEAR_NOTIFICATIONS': () => Promise.resolve({ default: ConfirmClearNotificationsModal }),
  'CONFIRM_LOG_OUT': () => Promise.resolve({ default: ConfirmLogOutModal }),
  'CONFIRM_FOLLOW_TO_LIST': () => Promise.resolve({ default: ConfirmFollowToListModal }),
  'CONFIRM_MISSING_ALT_TEXT': () => Promise.resolve({ default: ConfirmMissingAltTextModal }),
  'MUTE': MuteModal,
  'BLOCK': BlockModal,
  'DOMAIN_BLOCK': DomainBlockModal,
  'REPORT': ReportModal,
  'ACTIONS': () => Promise.resolve({ default: ActionsModal }),
  'EMBED': EmbedModal,
  'FOCAL_POINT': () => Promise.resolve({ default: AltTextModal }),
  'IMAGE_EDIT': () => Promise.resolve({ default: ImageEditModal }),
  'LIST_ADDER': ListAdder,
  'COMPARE_HISTORY': CompareHistoryModal,
  'FILTER': FilterModal,
  'SUBSCRIBED_LANGUAGES': SubscribedLanguagesModal,
  'INTERACTION': InteractionModal,
  'CLOSED_REGISTRATIONS': ClosedRegistrationsModal,
  'IGNORE_NOTIFICATIONS': IgnoreNotificationsModal,
  'ANNUAL_REPORT': AnnualReportModal,
  'LAOID_PROFILE': () => Promise.resolve({ default: LaoIdProfileModal }),
  'COMPOSE_FORM': () => Promise.resolve({ default: ComposeFormModal }),
  'FAVOURITES': () => Promise.resolve({ default: FavouritesModal }),
  'REPORT_SENSITIVE': () => Promise.resolve({ default: ReportSensitiveModal }),
  'FOLLOWERS': () => Promise.resolve({ default: FollowersModal }),
  'SYNC_STATUS': () => Promise.resolve({ default: SyncModal }),
  'LANGUAGE': () => Promise.resolve({ default: LanguageModal }),
  'THEME': () => Promise.resolve({ default: ThemeModal }),
};

class ModalRoot extends PureComponent {
  static propTypes = {
    type: PropTypes.string,
    props: PropTypes.object,
    onClose: PropTypes.func.isRequired,
    ignoreFocus: PropTypes.bool,
    enableDragToDismiss: PropTypes.bool,
    dragOnMobileOnly: PropTypes.bool,
    dragThreshold: PropTypes.number,
    dispatch: PropTypes.func.isRequired,
    ...WithOptionalRouterPropTypes,
  };

  static defaultProps = {
    enableDragToDismiss: true,
    dragOnMobileOnly: true,
    dragThreshold: 150,
  };

  state = {
    backgroundColor: null,
    isDragging: false,
    dragY: 0,
    startY: 0,
    startTime: 0,
    isAnimating: false,
    animationState: "closed",
    isRouteSuspended: false,
  };

  componentDidMount() {
    this.detectMobile();
  }

  detectMobile = () => {
    const isMobile =  window.innerWidth <= 768;

    this.setState({ isMobile });
  };

  getSnapshotBeforeUpdate(prevProps) {
    const wasVisible = !!prevProps.type;
    const isVisible = !!this.props.type;
    const isMobile = window.innerWidth < 630;

    return {
      visible: isVisible,
      wasVisible: wasVisible,
      shouldAnimate: !wasVisible && isVisible && isMobile
    };
  }

  componentDidUpdate(prevProps, prevState, { visible, wasVisible, shouldAnimate }) {
    if (visible && !wasVisible && shouldAnimate) {
      document.body.classList.add("with-modals--active");
      document.documentElement.style.marginRight = `${getScrollbarWidth()}px`;
      this.addDragListeners();

      this.startOpenAnimation();
    } else if (!visible && wasVisible && shouldAnimate) {
      document.body.classList.remove("with-modals--active");
      document.documentElement.style.marginRight = "0";
      this.removeDragListeners();
    }

    this.updateRouteSuspension(prevProps);
  }

  componentWillUnmount() {
    this.removeDragListeners();
  }

  startOpenAnimation = () => {
    this.setState({
      isAnimating: true,
      animationState: "opening",
    });

    requestAnimationFrame(() => {
      const modalContainer = document.querySelector(".modal-root__container");
      if (modalContainer) {
        modalContainer.style.transform = `translateY(100%)`;
        modalContainer.style.opacity = "0";

        requestAnimationFrame(() => {
          modalContainer.style.transition = "transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.4s ease-out";
          modalContainer.style.transform = "translateY(0)";
          modalContainer.style.opacity = "1";

          setTimeout(() => {
            this.setState({
              isAnimating: false,
              animationState: "open",
            });
            if (modalContainer) {
              modalContainer.style.transition = "";
            }
          }, 400);
        });
      }
    });
  };

  startCloseAnimation = (callback) => {
    this.setState({
      isAnimating: true,
      animationState: "closing",
    });

    const modalContainer = document.querySelector(".modal-root__container");
    if (modalContainer) {

      requestAnimationFrame(() => {
        modalContainer.style.transition =
          "transform 0.35s cubic-bezier(0.55, 0.055, 0.675, 0.19), opacity 0.35s ease-in";
        modalContainer.style.transform = "translateY(100%)";
        modalContainer.style.opacity = "0";

        setTimeout(() => {
          this.setState({
            isAnimating: false,
            animationState: "closed",
          });
          if (callback) callback();
        }, 350);
      });
    } else {
      if (callback) callback();
    }
  };

  shouldEnableDrag = () => {
    const { enableDragToDismiss, dragOnMobileOnly, type } = this.props;
    const { isMobile } = this.state;
    const noDragTypes = ['MEDIA', 'AUDIO', 'VIDEO', 'IMAGE'];

    if (noDragTypes.includes(type)) return false;
    if (!enableDragToDismiss) return false;
    if (dragOnMobileOnly && !isMobile) return false;

    return true;
  };

  shouldSuspendOnRouteChange = () => {
    return ['MEDIA', 'AUDIO', 'VIDEO'].includes(this.props.type);
  };

  updateRouteSuspension = (prevProps) => {
    const { location, type } = this.props;
    const prevLocation = prevProps.location;

    if (!type || type !== prevProps.type) {
      if (this.state.isRouteSuspended) {
        this.setState({ isRouteSuspended: false });
      }

      return;
    }

    if (!this.shouldSuspendOnRouteChange() || !location || !prevLocation || location === prevLocation) {
      return;
    }

    const previousModalKey = prevLocation.state?.mastodonModalKey;
    const currentModalKey = location.state?.mastodonModalKey;

    if (previousModalKey && currentModalKey !== previousModalKey) {
      if (!this.state.isRouteSuspended) {
        this.setState({ isRouteSuspended: true });
      }
    } else if (currentModalKey && this.state.isRouteSuspended) {
      this.setState({ isRouteSuspended: false });
    }
  };

  addDragListeners = () => {
    if (!this.shouldEnableDrag()) return;

    document.addEventListener("touchstart", this.handleTouchStart, { passive: false });
    document.addEventListener("touchmove", this.handleTouchMove, { passive: false });
    document.addEventListener("touchend", this.handleTouchEnd);

    document.addEventListener("mousedown", this.handleMouseDown);
    document.addEventListener("mousemove", this.handleMouseMove);
    document.addEventListener("mouseup", this.handleMouseUp);
  };

  removeDragListeners = () => {
    document.removeEventListener("touchstart", this.handleTouchStart);
    document.removeEventListener("touchmove", this.handleTouchMove);
    document.removeEventListener("touchend", this.handleTouchEnd);
    document.removeEventListener("mousedown", this.handleMouseDown);
    document.removeEventListener("mousemove", this.handleMouseMove);
    document.removeEventListener("mouseup", this.handleMouseUp);
  };

  handleTouchStart = (e) => {
    if (!this.shouldStartDrag(e.target)) return;

    const touch = e.touches[0];
    this.startDrag(touch.clientY);
  };

  handleMouseDown = (e) => {
    if (!this.shouldStartDrag(e.target)) return;

    this.startDrag(e.clientY);
  };

  shouldStartDrag = (target) => {
    const modalContent = document.querySelector(".modal-root__container");
    if (!modalContent) return false;

    const interactiveElements = ["BUTTON", "INPUT", "TEXTAREA", "SELECT", "A"];
    if (interactiveElements.includes(target.tagName)) return false;

    const nonDraggableClasses = ["button", "btn", "link", "close"];
    if (nonDraggableClasses.some((cls) => target.classList.contains(cls))) return false;

    return modalContent.contains(target);
  };

  startDrag = (clientY) => {
    this.setState({
      isDragging: true,
      startY: clientY,
      startTime: Date.now(),
      dragY: 0,
    });
  };

  handleTouchMove = (e) => {
    if (!this.state.isDragging) return;

    // e.preventDefault();
    const touch = e.touches[0];
    this.updateDrag(touch.clientY);
  };

  handleMouseMove = (e) => {
    if (!this.state.isDragging) return;

    this.updateDrag(e.clientY);
  };

  updateDrag = (clientY) => {
    const deltaY = Math.max(0, clientY - this.state.startY);
    this.setState({ dragY: deltaY });
    this.applyDragTransform(deltaY);
  };

  handleTouchEnd = () => {
    this.endDrag();
  };

  handleMouseUp = () => {
    this.endDrag();
  };

  endDrag = () => {
    if (!this.state.isDragging) return;

    const { dragY, startTime } = this.state;
    const { dragThreshold } = this.props;
    const duration = Date.now() - startTime;
    const velocity = dragY / duration;

    const shouldDismiss = dragY > dragThreshold || velocity > 0.5;

    if (shouldDismiss) {
      this.animateOut();
    } else {
      this.animateBack();
    }

    this.setState({
      isDragging: false,
      dragY: 0,
      startY: 0,
      startTime: 0,
    });
  };

  applyDragTransform = (dragY) => {
    const modalContainer = document.querySelector(".modal-root__container");
    if (!modalContainer) return;

    const opacity = Math.max(0.3, 1 - dragY / (this.props.dragThreshold * 2));
    const scale = Math.max(0.95, 1 - dragY / (this.props.dragThreshold * 10));

    modalContainer.style.transform = `translateY(${dragY}px) scale(${scale})`;
    modalContainer.style.opacity = opacity;
  };

  animateOut = () => {
    this.startCloseAnimation(() => {

      const { onClose, type, dispatch } = this.props;
      const message = this._modal?.getCloseConfirmationMessage?.();

      if (type === "COMPOSE_FORM") {
        dispatch(resetCompose());
      }

      onClose(message, true);
    });
  };

  animateBack = () => {
    const modalContainer = document.querySelector(".modal-root__container");
    if (!modalContainer) return;

    modalContainer.style.transition = "transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease-out";
    modalContainer.style.transform = "translateY(0px) scale(1)";
    modalContainer.style.opacity = "1";

    setTimeout(() => {
      if (modalContainer) {
        modalContainer.style.transition = "";
      }
    }, 300);
  };

  setBackgroundColor = color => {
    this.setState({ backgroundColor: color });
  };

  suspendMediaModal = () => {
    if (this.shouldSuspendOnRouteChange() && !this.state.isRouteSuspended) {
      this.setState({ isRouteSuspended: true });
    }
  };

  renderLoading = () => {
    const { onClose } = this.props;

    return <ModalPlaceholder loading onClose={onClose} />;
  };

  renderError = (props) => {
    const { onClose } = this.props;

    return <ModalPlaceholder {...props} onClose={onClose} />;
  };

  handleClose = (ignoreFocus = false) => {
    const { onClose, type, dispatch } = this.props;
    const { animationState, isAnimating } = this.state;
    const message = this._modal?.getCloseConfirmationMessage?.();

    document.body.classList.remove('with-modals--active');

    if (type === 'COMPOSE_FORM') {
      dispatch(resetCompose());
    }

    if(type === 'IMAGE_EDIT' || type === 'CONFIRM_EDIT_STATUS' || type === 'CONFIRM') {
      onClose(message, ignoreFocus, type);
    } else if (animationState === "open" && !isAnimating) {
      this.startCloseAnimation(() => {
        onClose(message, ignoreFocus);
      });
    } else if (animationState === "opening" && isAnimating) {
      setTimeout(() => {
        this.startCloseAnimation(() => {
          onClose(message, ignoreFocus);
        });
      }, 100);
    } else {
      onClose(message, ignoreFocus);
    }
  };

  setModalRef = (c) => {
    this._modal = c;
  };

  render() {
    const { type, props, ignoreFocus, enableDragToDismiss } = this.props;
    const { backgroundColor, isDragging, isAnimating, animationState, isRouteSuspended } = this.state;

    const visible = !!type;
    const renderModalContent = visible && !isRouteSuspended;
    const shouldShowDragHandle = this.shouldEnableDrag();

    const modalClasses = [
      isDragging ? "modal-root--dragging" : "",
      enableDragToDismiss ? "modal-root--draggable" : "",
      isAnimating ? "modal-root--animating" : "",
      `modal-root--${animationState}`,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <Base
        backgroundColor={backgroundColor}
        onClose={this.handleClose}
        ignoreFocus={ignoreFocus}
        suspended={isRouteSuspended}
        className={modalClasses}
      >
        {visible && (
          <>
            {renderModalContent && (
              <BundleContainer
                fetchComponent={MODAL_COMPONENTS[type]}
                loading={this.renderLoading}
                error={this.renderError}
                renderDelay={200}
              >
                {(SpecificComponent) => {
                  return (
                    <>
                      {shouldShowDragHandle && (
                        <div className='modal-drag-handle'>
                          <div className='modal-drag-indicator' />
                        </div>
                      )}
                      <SpecificComponent
                        {...props}
                        onChangeBackgroundColor={this.setBackgroundColor}
                        onNavigateAway={this.suspendMediaModal}
                        onClose={this.handleClose}
                        ref={this.setModalRef}
                      />
                    </>
                  );
                }}
              </BundleContainer>
            )}

            <Helmet>
              <meta name='robots' content='noindex' />
            </Helmet>
          </>
        )}
      </Base>
    );
  }

}
export default connect()(withOptionalRouter(ModalRoot));
